import React, { useCallback, useState } from 'react';
import { EditPaymentMethodProvider, ManagePaymentMethods as FSUIManagePaymentMethods } from '@shipstation/financial-services-ui-components';
import type {
  ManagePaymentMethodsProps as FSUIManagePaymentMethodsProps,
  StoredPaymentMethod,
} from '@shipstation/financial-services-ui-components';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import { AddPaymentMethodModal } from './add-payment-method-modal';
import { ManagePaymentFlow } from './constants';
import { EditPaymentMethodModal } from './edit-payment-method-modal';
import { ManageModal } from './manage-modal';
import { assignedPreferencesMessages, messages } from './messages';
import { RemovePaymentMethodModal } from './remove-payment-method-modal';
import { useFsuiContext } from '../../../../../financial-services-ui/fsui-context-provider';
import { useModal } from '../../../../../hooks/use-modal';

import type { AuctanePaySession } from '../../../../../financial-services-ui/types/fsui';

type FlowModalProps = {
  currentFlow?: ManagePaymentFlow;
  onCancel: () => void;
  onConfirm: () => void;
  paymentMethod?: StoredPaymentMethod;
};

const FlowModal = ({ currentFlow, onCancel, onConfirm, paymentMethod }: FlowModalProps) => {
  switch (currentFlow) {
    case ManagePaymentFlow.Add:
      return <AddPaymentMethodModal onCancel={onCancel} onConfirm={onConfirm} />;
    case ManagePaymentFlow.Edit:
      return (
        <EditPaymentMethodProvider paymentMethod={paymentMethod!}>
          <EditPaymentMethodModal onCancel={onCancel} onConfirm={onConfirm} />
        </EditPaymentMethodProvider>
      );
    case ManagePaymentFlow.Remove:
      return <RemovePaymentMethodModal onClose={onCancel} onConfirm={onConfirm} paymentMethod={paymentMethod!} />;
    default:
      return null;
  }
};

type FSUIPassthroughProps = Pick<FSUIManagePaymentMethodsProps, 'activePaymentMethods' | 'allPaymentMethods'>;

type ManagePaymentMethodsProps = FSUIPassthroughProps & {
  onClose: () => void;
  onConfirm: () => void;
  preloadedSession?: AuctanePaySession | null;
  isLoading?: boolean;
};

export const ManagePaymentMethods = ({ onClose, onConfirm, preloadedSession, isLoading = false, ...rest }: ManagePaymentMethodsProps) => {
  const { createPaymentSession } = useFsuiContext();
  const [currentFlow, setCurrentFlow] = useState<ManagePaymentFlow | undefined>(undefined);
  const { isOpen, open, close } = useModal();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<StoredPaymentMethod | undefined>(undefined);

  const handleOpenModal = useCallback(
    (flow: ManagePaymentFlow, paymentMethod?: StoredPaymentMethod) => {
      setSelectedPaymentMethod(paymentMethod);
      setCurrentFlow(flow);
      open();
    },
    [open],
  );

  const onOpenRemoveModal = useCallback(
    (paymentMethod: StoredPaymentMethod) => {
      handleOpenModal(ManagePaymentFlow.Remove, paymentMethod);
    },
    [handleOpenModal],
  );

  const onOpenEditModal = useCallback(
    (paymentMethod: StoredPaymentMethod) => {
      handleOpenModal(ManagePaymentFlow.Edit, paymentMethod);
    },
    [handleOpenModal],
  );

  const onOpenAddModal = useCallback(() => {
    if (!preloadedSession) {
      createPaymentSession(true);
    }
    handleOpenModal(ManagePaymentFlow.Add);
  }, [preloadedSession, handleOpenModal, createPaymentSession]);

  const handleOnConfirm = useCallback(() => {
    close();
    onConfirm();
  }, [close, onConfirm]);

  return (
    <>
      <ManageModal cancelMessage={messages.doneButton} headerMessage={messages.header} onCancel={onClose} onClose={onClose}>
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <FSUIManagePaymentMethods
            assignedPreferencesMessages={assignedPreferencesMessages}
            onAdd={onOpenAddModal}
            onDelete={onOpenRemoveModal}
            onEdit={onOpenEditModal}
            {...rest}
          />
        )}
      </ManageModal>
      {isOpen && <FlowModal currentFlow={currentFlow} onCancel={close} onConfirm={handleOnConfirm} paymentMethod={selectedPaymentMethod} />}
    </>
  );
};
